"use client";

import { useState } from 'react';
import useSWR from 'swr';
import type { 
  CreateWorkspaceRequest, 
  CreateWorkspaceResponse, 
  GetWorkspacesResponse,
} from '../lib/api/types';
import { createWorkspace } from '../lib/api/workspaces';
import { useUser } from './use-user';

// Fetcher function for SWR
const fetcher = async (url: string): Promise<GetWorkspacesResponse> => {
  const response = await fetch(url);
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch');
  }
  return response.json();
};

/**
 * React hook for fetching user's workspaces
 */
export function useWorkspaces() {
  const { user } = useUser();
  
  const { data, error, isLoading, isValidating, mutate } = useSWR<GetWorkspacesResponse>(
    user ? '/api/workspaces' : null,
    fetcher,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data?.workspaces,
    error,
    isLoading: isLoading && !data, // Only show loading on initial load, not revalidation
    isValidating,
    mutate,
  };
}

/**
 * React hook for creating workspaces with loading and error states
 */
export function useCreateWorkspace() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (data: CreateWorkspaceRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await createWorkspace(data);
      setIsLoading(false);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create workspace';
      setError(errorMessage);
      setIsLoading(false);
      throw err;
    }
  };

  return {
    create,
    isLoading,
    error,
    clearError: () => setError(null),
  };
} 